import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import MDButton from "components/MDButton";
import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import { api, get, post, put } from "lib/api";
import {
  coreReturnLifecycleLabel,
  coreReturnWorkflowSteps,
  resolveCoreReturnLifecycleStatus,
} from "lib/coreReturnLifecycle";
import { useAuth } from "context/AuthContext";
import { isCustomerRole, isDistributorRole, isPortalRole } from "lib/portalRoles";
import {
  addressRowKey,
  applyAddressCandidate,
  createAddressRow,
  isAddressRowFilled,
  isVerifiedAddressRow,
  normalizeAddressRow,
  stripAddressRowForSave,
  useStorefrontAddressLookup,
} from "lib/storefrontAddressLookup";
import { useMobileLocale } from "mobile/i18n";
import {
  AccountActionTile,
  AccountChipRow,
  AccountDetailRow,
  AccountFilterBar,
  AccountIdentity,
  AccountListGroup,
  AccountListRow,
  AccountNotice,
  AccountRecordCard,
  AccountSection,
  PortalAccountShell,
  QuickActionBar,
  accountFieldSx,
  accountPrimaryBtnSx,
  accountSecondaryBtnSx,
  buildAccountQuickActions,
  portalChrome,
} from "mobile/portal/PortalAccountChrome";

const COLORS = {
  bg: portalChrome.canvas,
  ink: portalChrome.ink,
  muted: portalChrome.muted,
  line: portalChrome.line,
  accent: portalChrome.accent,
  red: portalChrome.brandRed,
  green: "#147A5C",
};

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: String(currency || "USD").toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function pretty(value, fallback = "Pending") {
  const text = String(value || fallback).replaceAll("_", " ");
  return text.replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeEntity(row) {
  if (!row) return null;
  if (row.data) return normalizeEntity(row.data);
  if (row.attributes) return { id: row.id, documentId: row.documentId || row.id, ...row.attributes };
  return row;
}

function dateText(value) {
  if (!value) return "Pending";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
}

function usePortalSession() {
  const auth = useAuth();
  const role = String(auth.pluginRole || "").toLowerCase();
  return {
    ...auth,
    role,
    isPortal: isPortalRole(role),
    isCustomer: isCustomerRole(role),
    isDistributor: isDistributorRole(role),
  };
}

function Guard({ children, customerOnly = false, distributorOnly = false }) {
  const location = useLocation();
  const session = usePortalSession();
  if (!session.token) return <Navigate to="/m/authentication/sign-in" state={{ from: location }} replace />;
  if (session.authLoading || session.pluginRole == null) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", bgcolor: COLORS.bg }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  // Non-portal (including staff) → post-login; staff are redirected to staff-erp there.
  if (!session.isPortal) return <Navigate to="/m/post-login" replace />;
  if (customerOnly && !session.isCustomer) return <Navigate to="/m/account" replace />;
  if (distributorOnly && !session.isDistributor) return <Navigate to="/m/account" replace />;
  return children;
}

function Shell({ title, subtitle, children, action, hub = false }) {
  return (
    <PortalAccountShell title={title} subtitle={subtitle} action={action} hub={hub}>
      {children}
    </PortalAccountShell>
  );
}

function Tile({ icon, title, text, onClick, tone = "ink" }) {
  return (
    <AccountActionTile
      icon={icon}
      title={title}
      text={text}
      onClick={onClick}
      tone={tone === "red" || tone === "danger" ? "red" : "ink"}
    />
  );
}

function RowCard({ title, meta, chips, onClick, children }) {
  return (
    <AccountRecordCard title={title} meta={meta} chips={chips} onClick={onClick}>
      {children}
    </AccountRecordCard>
  );
}

function LoadingOrError({ query, emptyText, hasRows }) {
  return <AccountNotice query={query} emptyText={emptyText} hasRows={hasRows} />;
}

function DetailRow({ label, value, first = false }) {
  return <AccountDetailRow label={label} value={value} first={first} />;
}

Guard.propTypes = {
  children: PropTypes.node.isRequired,
  customerOnly: PropTypes.bool,
  distributorOnly: PropTypes.bool,
};

Guard.defaultProps = {
  customerOnly: false,
  distributorOnly: false,
};

Shell.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  hub: PropTypes.bool,
};

Shell.defaultProps = {
  title: "",
  subtitle: "",
  action: null,
  hub: false,
};

Tile.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  tone: PropTypes.string,
};

Tile.defaultProps = {
  tone: "ink",
};

RowCard.propTypes = {
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  chips: PropTypes.arrayOf(PropTypes.node),
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

RowCard.defaultProps = {
  meta: null,
  chips: [],
  children: null,
};

LoadingOrError.propTypes = {
  query: PropTypes.shape({
    isLoading: PropTypes.bool,
    error: PropTypes.shape({
      message: PropTypes.string,
      response: PropTypes.shape({
        data: PropTypes.shape({
          error: PropTypes.shape({
            message: PropTypes.string,
          }),
        }),
      }),
    }),
  }),
  emptyText: PropTypes.string.isRequired,
  hasRows: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
};

LoadingOrError.defaultProps = {
  query: null,
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  first: PropTypes.bool,
};

DetailRow.defaultProps = {
  value: "Pending",
  first: false,
};

export function MobilePortalHome() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const meQuery = useQuery({
    queryKey: ["m-portal-me"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!session.token && session.isPortal,
    staleTime: 15000,
  });
  const customer = meQuery.data?.data || null;
  const profile = meQuery.data?.profile || null;
  const displayName = customer?.name || profile?.displayName || "";
  const email = profile?.email || customer?.emails?.[0] || "";
  const roleLabel = session.isDistributor ? t("Distributor") : t("Customer");
  const quickActions = buildAccountQuickActions({
    onLogout: () => {
      session.logout();
      navigate("/m", { replace: true });
    },
  });

  return (
    <Guard>
      <Shell hub>
        <AccountIdentity name={displayName} email={email} roleLabel={roleLabel} />
        <QuickActionBar actions={quickActions} />

        {session.isDistributor ? (
          <AccountListGroup title="Distributor">
            <AccountListRow
              icon="rule"
              title="Order requests"
              subtitle="Approve requests or send them to VBS."
              onClick={() => navigate("/m/account/distributor-requests")}
            />
            <AccountListRow
              icon="groups"
              title="Linked customers"
              subtitle="Customers assigned to your account."
              onClick={() => navigate("/m/account/distributor-customers")}
              divider={false}
            />
          </AccountListGroup>
        ) : null}

        <AccountListGroup title="Orders & service">
          <AccountListRow
            icon="receipt_long"
            title="Orders"
            subtitle="Status, totals, invoices, and tracking."
            onClick={() => navigate("/m/account/orders")}
          />
          <AccountListRow
            icon="qr_code_scanner"
            title={session.isDistributor ? "Sold valve bodies" : "Valve bodies"}
            subtitle={
              session.isDistributor
                ? "Track sold and registered units."
                : "Warranty, core returns, and registrations."
            }
            onClick={() => navigate("/m/account/valve-bodies")}
          />
          {!session.isDistributor ? (
            <AccountListRow
              icon="replay"
              title={t("Core returns")}
              subtitle={t("Return owed cores with a prepaid label.")}
              onClick={() => navigate("/m/account/core-returns")}
            />
          ) : null}
          <AccountListRow
            icon="forum"
            title={t("Support")}
            subtitle={t("Chat with VBS — messages and photos.")}
            onClick={() => navigate("/m/account/support")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup title="Account">
          <AccountListRow
            icon="person"
            title="Profile"
            subtitle="Contact details and account information."
            onClick={() => navigate("/m/account/profile")}
          />
          <AccountListRow
            icon="location_on"
            title="Addresses"
            subtitle="Saved shipping addresses for checkout."
            onClick={() => navigate("/m/account/addresses")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup title="Shop">
          <AccountListRow
            icon="shopping_bag"
            title="Product catalog"
            subtitle="Browse products and place orders."
            onClick={() => navigate("/m/catalog")}
          />
          <AccountListRow
            icon="shopping_cart"
            title="Cart"
            subtitle="Review selected catalog items."
            onClick={() => navigate("/m/cart")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup>
          <AccountListRow
            icon="logout"
            title="Sign out"
            danger
            divider={false}
            onClick={() => {
              session.logout();
              navigate("/m", { replace: true });
            }}
          />
        </AccountListGroup>
      </Shell>
    </Guard>
  );
}

export function MobilePortalOrders() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const query = useQuery({ queryKey: ["m-portal-orders"], queryFn: () => get("/api/storefront/orders", { pagination: { page: 1, pageSize: 40 } }), enabled: !!session.token && session.isPortal, staleTime: 15000 });
  const rows = Array.isArray(query.data?.data) ? query.data.data : [];
  return (
    <Guard>
      <Shell title="Orders" subtitle="Status, payment, and tracking.">
        <LoadingOrError query={query} emptyText="No orders yet." hasRows={rows.length} />
        {rows.map((order) => (
          <RowCard
            key={String(order.documentId || order.id)}
            title={order.salesOrderNumber || order.documentId || t("Order")}
            meta={order.trackingNumber ? `${t("Tracking")} ${order.trackingNumber}` : dateText(order.createdAt)}
            chips={[pretty(order.status), pretty(order.paymentStatus), money(order.total)]}
            onClick={() => {
              if (order.documentId) navigate(`/m/account/orders/${order.documentId}`);
            }}
          />
        ))}
      </Shell>
    </Guard>
  );
}

export function MobilePortalOrderDetail() {
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null);
  const query = useQuery({ queryKey: ["m-portal-order", documentId], queryFn: () => get(`/api/storefront/orders/${documentId}`), enabled: !!session.token && session.isPortal && !!documentId, staleTime: 15000 });
  const order = query.data?.data || null;
  const items = Array.isArray(order?.items) ? order.items : [];
  const invoiceArchive = normalizeEntity(order?.invoiceArchive);
  const invoiceId = String(invoiceArchive?.documentId || "").trim();
  const paymentStatus = String(order?.paymentStatus || "").toLowerCase();
  const orderStatus = String(order?.status || "").toLowerCase();
  const isBankTransfer =
    String(order?.paymentCollectionMethod || order?.paymentProvider || "")
      .trim()
      .toLowerCase() === "bank_transfer";
  const canCancelUnpaidBankTransfer =
    !!order &&
    isBankTransfer &&
    !["paid", "processing", "refunded"].includes(paymentStatus) &&
    orderStatus !== "cancelled" &&
    orderStatus !== "closed" &&
    String(order?.documentType || "sales_order").toLowerCase() === "sales_order";

  const cancelUnpaidBankTransfer = async () => {
    if (!documentId || !canCancelUnpaidBankTransfer) return;
    if (
      !window.confirm(
        "Cancel this unpaid bank transfer order? Reserved inventory will be released.",
      )
    ) {
      return;
    }
    setCancelLoading(true);
    setCancelMessage(null);
    try {
      await post(`/api/storefront/orders/${documentId}/cancel-unpaid-bank-transfer`, {});
      setCancelMessage({ type: "success", text: "Order cancelled." });
      await query.refetch();
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to cancel order";
      setCancelMessage({ type: "error", text: msg });
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <Guard>
      <Shell title={order?.salesOrderNumber || "Order"} subtitle="Details and tracking.">
        <LoadingOrError query={query} emptyText="Order not found." hasRows={!!order} />
        {cancelMessage ? (
          <Alert severity={cancelMessage.type} sx={{ mb: 1.2 }}>
            {cancelMessage.text}
          </Alert>
        ) : null}
        {order ? (
          <>
            <AccountSection title="Summary">
              <AccountChipRow
                chips={[
                  { label: pretty(order.status), emphasis: true },
                  pretty(order.paymentStatus),
                  money(order.total),
                ]}
              />
              <Box sx={{ mt: 0.5 }}>
                <DetailRow label="Shipping" value={order.shippingService || "Pending"} first />
                <DetailRow label="Tracking" value={order.trackingNumber || "Pending"} />
                <DetailRow label="Ship window" value={dateText(order.shipByDate || order.requestedShipDate)} />
                <DetailRow label="Subtotal" value={money(order.subtotal, order.currency)} />
                <DetailRow label="Shipping amount" value={money(order.shippingAmount, order.currency)} />
                <DetailRow label="Payment" value={pretty(order.paymentMethod || order.paymentStatus)} />
              </Box>
            </AccountSection>
            {order.bankTransferInstructionsSnapshot ? (
              <AccountSection title={t("Bank transfer")}>
                <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 13.5, color: portalChrome.ink }}>
                  {order.bankTransferInstructionsSnapshot}
                </Typography>
              </AccountSection>
            ) : null}
            {canCancelUnpaidBankTransfer ? (
              <Box sx={{ px: 0.5, pb: 1 }}>
                <MDButton
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={cancelLoading}
                  onClick={cancelUnpaidBankTransfer}
                  sx={{ borderRadius: "10px", py: 1.1, fontWeight: 800 }}
                >
                  {cancelLoading ? "Cancelling…" : "Cancel unpaid order"}
                </MDButton>
              </Box>
            ) : null}
            {invoiceArchive ? (
              <AccountSection title="Invoice">
                <DetailRow
                  label="Status"
                  value={pretty(invoiceArchive.status || invoiceArchive.deliveryStatus || "Available")}
                  first
                />
                {invoiceId ? (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }}>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      sx={accountSecondaryBtnSx}
                      onClick={() =>
                        window.open(`/api/storefront/invoices/${invoiceId}/cfdi/pdf`, "_blank", "noopener,noreferrer")
                      }
                    >
                      {t("PDF")}
                    </MDButton>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      sx={accountSecondaryBtnSx}
                      onClick={() =>
                        window.open(`/api/storefront/invoices/${invoiceId}/cfdi/xml`, "_blank", "noopener,noreferrer")
                      }
                    >
                      {t("XML")}
                    </MDButton>
                  </Stack>
                ) : null}
              </AccountSection>
            ) : null}
            {items.length ? (
              <AccountListGroup title="Line items">
                {items.map((item, index) => (
                  <Box
                    key={String(item.documentId || item.id)}
                    sx={{
                      px: 1.35,
                      py: 1.2,
                      borderBottom: index < items.length - 1 ? `1px solid ${portalChrome.line}` : "none",
                    }}
                  >
                    <Typography sx={{ fontWeight: 750, fontSize: 14.5, color: portalChrome.ink }}>
                      {item.inventoryItem?.sku ? `${item.inventoryItem.sku} ` : ""}
                      {item.inventoryItem?.name || item.notes || "Item"}
                    </Typography>
                    <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 0.3, fontWeight: 600 }}>
                      {t("Qty")} {item.quantity || 1} · {t("Unit")} {money(item.unitPrice)} · {t("Core")}{" "}
                      {money(item.coreCharge)}
                    </Typography>
                  </Box>
                ))}
              </AccountListGroup>
            ) : null}
          </>
        ) : null}
      </Shell>
    </Guard>
  );
}

export function MobilePortalValveBodies() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const query = useQuery({ queryKey: ["m-portal-valve-bodies", session.role], queryFn: () => get("/api/storefront/valve-bodies/me"), enabled: !!session.token && session.isPortal, staleTime: 15000 });
  const rows = Array.isArray(query.data?.data) ? query.data.data : [];
  const filteredRows = rows.filter((unit) => {
    const lifecycle = unit.lifecycle || {};
    const haystack = [unit.partNumber, unit.productName, unit.displayTag, unit.tag, unit.serialNumber, lifecycle.progressStatus]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch = !search.trim() || haystack.includes(search.trim().toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "core_due" && lifecycle.coreDue) ||
      (filter === "registered" && lifecycle.registered) ||
      (filter === "unregistered" && !lifecycle.registered) ||
      (filter === "warranty" && lifecycle.activeWarranty);
    return matchesSearch && matchesFilter;
  });
  const filters = [
    { key: "all", label: t("All"), icon: "filter_list" },
    { key: "core_due", label: t("Core due"), icon: "assignment_return", accent: "orange" },
    { key: "registered", label: t("Registered"), icon: "check_circle", accent: "green" },
    { key: "unregistered", label: t("Not registered"), icon: "radio_button_unchecked" },
    { key: "warranty", label: t("Warranty"), icon: "verified_user", accent: "red" },
  ];
  return (
    <Guard>
      <Shell
        title={session.isDistributor ? "Sold valve bodies" : "Valve bodies"}
        subtitle={session.isDistributor ? "Sold units and registration." : "Warranty, cores, and lifecycle."}
      >
        <TextField
          fullWidth
          label={t("Search valve bodies")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ ...accountFieldSx, mb: 1 }}
        />
        <Box sx={{ mb: 1.15 }}>
          <AccountFilterBar label={t("Show")} value={filter} options={filters} onChange={setFilter} />
        </Box>
        <LoadingOrError
          query={query}
          emptyText={rows.length ? "No valve bodies match those filters." : "No valve bodies found yet."}
          hasRows={filteredRows.length}
        />
        {filteredRows.map((unit) => {
          const lifecycle = unit.lifecycle || {};
          return (
            <RowCard
              key={String(unit.documentId || unit.id)}
              title={unit.partNumber || unit.displayTag || t("Valve Body")}
              meta={unit.tag ? `${t("Tag")} ${unit.tag}` : unit.serialNumber || t("Serial pending")}
              chips={[
                pretty(lifecycle.progressStatus),
                lifecycle.registered ? t("Registered") : t("Not registered"),
                lifecycle.coreDue ? t("Core due") : t("Core clear"),
              ]}
              onClick={() => {
                if (unit.documentId) navigate(`/m/account/valve-bodies/${unit.documentId}`);
              }}
            />
          );
        })}
      </Shell>
    </Guard>
  );
}

export function MobilePortalValveBodyDetail() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const detailQuery = useQuery({ queryKey: ["m-portal-valve-body", documentId], queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`), enabled: !!session.token && session.isPortal && !!documentId, staleTime: 15000 });
  const historyQuery = useQuery({ queryKey: ["m-portal-valve-body-history", documentId], queryFn: () => get(`/api/storefront/valve-bodies/${documentId}/warranty-history`), enabled: !!session.token && session.isCustomer && !!documentId, staleTime: 15000 });
  const unit = detailQuery.data?.data || null;
  const lifecycle = unit?.lifecycle || {};
  const activeClaimId = String(lifecycle?.activeWarranty?.documentId || "").trim();
  const activeClaimRef = String(lifecycle?.activeWarranty?.rmaNumber || activeClaimId || "").trim();
  const history = Array.isArray(historyQuery.data?.data) ? historyQuery.data.data : [];
  const vehicleText = [lifecycle.vehicleYear, lifecycle.vehicleMake, lifecycle.vehicleModel, lifecycle.vehicleTrim].filter(Boolean).join(" ");
  const scanSource = pretty(lifecycle.registrationCreatedVia || (lifecycle.qrIdentifier ? "scan" : ""));
  return (
    <Guard>
      <Shell
        title={unit?.partNumber || unit?.displayTag || "Valve Body"}
        subtitle={unit?.tag ? `${t("Tag")} ${unit.tag}` : unit?.serialNumber || "Unit lifecycle"}
      >
        <LoadingOrError query={detailQuery} emptyText="Valve body not found." hasRows={!!unit} />
        {unit ? (
          <>
            <AccountSection title="Unit">
              <AccountChipRow
                chips={[
                  { label: pretty(lifecycle.progressStatus), emphasis: true },
                  lifecycle.registered ? t("Registered") : t("Not registered"),
                  { label: lifecycle.coreDue ? t("Core due") : t("Core clear"), emphasis: !!lifecycle.coreDue },
                ]}
              />
              <Box sx={{ mt: 0.5 }}>
                <DetailRow label="Product" value={unit.productName || "Pending"} first />
                <DetailRow label="Part" value={unit.partNumber || "Pending"} />
                <DetailRow label="Serial" value={unit.serialNumber || unit.tag || "Pending"} />
                <DetailRow label="Transmission" value={unit.transmissionModel || "Pending"} />
                <DetailRow label="Core condition" value={pretty(unit.coreCondition || "Pending")} />
                <DetailRow label="Core deadline" value={dateText(lifecycle.coreDueAt)} />
                <DetailRow
                  label="Core status"
                  value={
                    lifecycle.coreReturnedAt
                      ? `${t("Returned")} ${dateText(lifecycle.coreReturnedAt)}`
                      : pretty(lifecycle.coreStatus || (lifecycle.coreDue ? "Due" : "Clear"))
                  }
                />
                {lifecycle.activeReturnRequest?.trackingNumber ? (
                  <DetailRow label="Return tracking" value={lifecycle.activeReturnRequest.trackingNumber} />
                ) : null}
              </Box>
            </AccountSection>

            <AccountSection title="Sale and install">
              <AccountChipRow
                chips={[
                  {
                    label: lifecycle.registered
                      ? t("Install registered")
                      : lifecycle.distributorScanAt
                      ? t("Sale scanned")
                      : lifecycle.sold
                      ? t("Sold")
                      : t("Awaiting sale"),
                    emphasis: !lifecycle.registered,
                  },
                ]}
              />
              <Box sx={{ mt: 0.35 }}>
                <DetailRow label="Sold as" value={pretty(lifecycle.soldToRole || "Pending")} first />
                <DetailRow label="Sold on" value={dateText(lifecycle.soldAt || unit.soldAt)} />
                <DetailRow label="Seller scan" value={dateText(lifecycle.distributorScanAt)} />
                <DetailRow
                  label="Install scan"
                  value={dateText(
                    lifecycle.installRegisteredAt || lifecycle.registeredAt || lifecycle.endUserRegistrationAt
                  )}
                />
                <DetailRow label="Sold to" value={lifecycle.soldToCustomer?.name || "Pending"} />
                <DetailRow label="Registered owner" value={lifecycle.registeredCustomer?.name || "Pending"} />
                <DetailRow
                  label="Registration status"
                  value={pretty(lifecycle.registrationStatus || (lifecycle.registered ? "Registered" : "Pending"))}
                />
                <DetailRow label="Scan source" value={scanSource || "Pending"} />
                <DetailRow
                  label="QR / tag"
                  value={lifecycle.qrIdentifier || unit.displayTag || unit.tag || unit.serialNumber || "Pending"}
                />
                <DetailRow label="Vehicle" value={vehicleText || "Pending"} />
                <DetailRow label="VIN" value={lifecycle.vehicleVin || "Pending"} />
                <DetailRow
                  label="Mileage"
                  value={lifecycle.vehicleMileage ? Number(lifecycle.vehicleMileage).toLocaleString() : "Pending"}
                />
                <DetailRow label="Core program" value={pretty(lifecycle.coreProgramPolicy || "Deposit program")} />
                <DetailRow
                  label="Warranty"
                  value={lifecycle.activeWarranty?.rmaNumber || pretty(lifecycle.activeWarranty?.status || "None")}
                />
              </Box>
            </AccountSection>

            {session.isCustomer ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1, mb: 1.15 }}>
                <Tile
                  icon="assignment_return"
                  title="Core return"
                  text={
                    lifecycle.activeReturnRequest
                      ? "Track the active core return."
                      : lifecycle.coreDue
                      ? "Start the refundable core return."
                      : "Review core status."
                  }
                  onClick={() => navigate(`/m/account/valve-bodies/${documentId}/core-return`)}
                  tone="red"
                />
                {activeClaimId ? (
                  <Tile
                    icon="verified"
                    title="Warranty"
                    text={`${t("Active claim")} ${activeClaimRef}`}
                    onClick={() => navigate(`/m/account/warranty-claims/${activeClaimId}`)}
                    tone="ink"
                  />
                ) : (
                  <Tile
                    icon="verified"
                    title="Warranty"
                    text="Create a warranty claim."
                    onClick={() => navigate(`/m/account/valve-bodies/${documentId}/warranty`)}
                    tone="ink"
                  />
                )}
              </Box>
            ) : null}

            {session.isCustomer ? (
              <>
                <Typography
                  sx={{
                    px: 0.5,
                    mb: 0.75,
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 0.9,
                    textTransform: "uppercase",
                    color: portalChrome.subtle,
                  }}
                >
                  {t("Warranty history")}
                </Typography>
                <LoadingOrError query={historyQuery} emptyText="No warranty claims yet." hasRows={history.length} />
                {history.map((claim) => (
                  <RowCard
                    key={String(claim.documentId || claim.id)}
                    title={claim.rmaNumber || claim.documentId}
                    meta={dateText(claim.createdAt)}
                    chips={[pretty(claim.status)]}
                    onClick={() => {
                      if (claim.documentId) navigate(`/m/account/warranty-claims/${claim.documentId}`);
                    }}
                  />
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </Shell>
    </Guard>
  );
}

async function uploadFiles(files) {
  const rows = Array.from(files || []).filter(Boolean);
  if (!rows.length) return [];
  const formData = new FormData();
  rows.forEach((file) => formData.append("files", file));
  const res = await api.post("/api/upload", formData);
  return (Array.isArray(res?.data) ? res.data : []).map((row) => Number(row?.id || 0)).filter((id) => id > 0);
}

function coreOptionKey(option) {
  return [
    option?.bookingMode || "label_only",
    option?.quoteId || "",
    option?.carrier || "",
    option?.service || "",
  ].join("|");
}

function CoreReturnProgressCard({ lifecycle, t, compact = false }) {
  const steps = coreReturnWorkflowSteps(lifecycle, (key, fallback) => t(fallback || key));
  const status = resolveCoreReturnLifecycleStatus(lifecycle);
  const statusLabel = coreReturnLifecycleLabel(lifecycle, (key, fallback) => t(fallback || key));
  const request = lifecycle?.activeReturnRequest || {};
  const tracking = String(request.trackingNumber || lifecycle?.activeReturnTrackingNumber || "").trim();
  const carrier = String(request.shippingCarrier || lifecycle?.activeReturnCarrier || "").trim();

  return (
    <Box
      sx={{
        borderRadius: "18px",
        bgcolor: portalChrome.surface,
        border: `1px solid ${portalChrome.line}`,
        boxShadow: portalChrome.lift,
        overflow: "hidden",
        mb: compact ? 0 : 1.15,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          background:
            "linear-gradient(135deg, rgba(224,24,32,0.10) 0%, rgba(255,255,255,0.96) 55%, rgba(20,122,92,0.08) 100%)",
          borderBottom: `1px solid ${portalChrome.line}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.7,
            textTransform: "uppercase",
            color: portalChrome.subtle,
          }}
        >
          {t("Return progress")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.55 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 820, color: portalChrome.ink, letterSpacing: -0.3 }}>
            {statusLabel}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "999px",
              bgcolor:
                status === "approved"
                  ? "rgba(20,122,92,0.12)"
                  : status === "forfeited"
                    ? "rgba(215,38,30,0.10)"
                    : portalChrome.brandRedSoft,
              color:
                status === "approved"
                  ? COLORS.green
                  : status === "forfeited"
                    ? portalChrome.danger
                    : portalChrome.brandRed,
              fontSize: 11.5,
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </Box>
        </Box>
        {(carrier || tracking) && (
          <Typography sx={{ mt: 0.55, fontSize: 12.5, fontWeight: 650, color: portalChrome.muted }}>
            {[carrier, tracking].filter(Boolean).join(" · ")}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 1.35 }}>
        {steps.map((row, index) => {
          const active = !!row.current;
          const done = !!row.done;
          const lineDone = done || (active && index < steps.length - 1);
          return (
            <Box key={row.key} sx={{ display: "flex", gap: 1.1, minHeight: index === steps.length - 1 ? 28 : 44 }}>
              <Box sx={{ width: 22, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: done || active ? (active ? portalChrome.brandRed : COLORS.green) : portalChrome.canvas,
                    border: `1.5px solid ${
                      done || active ? (active ? portalChrome.brandRed : COLORS.green) : portalChrome.line
                    }`,
                    color: done || active ? "#fff" : portalChrome.subtle,
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 14 }}>{done ? "check" : active ? "local_shipping" : "circle"}</Icon>
                </Box>
                {index < steps.length - 1 ? (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      mt: 0.35,
                      mb: 0.35,
                      borderRadius: 99,
                      bgcolor: lineDone ? "rgba(20,122,92,0.45)" : portalChrome.line,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ pt: 0.1, pb: index === steps.length - 1 ? 0 : 1.1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: active || done ? 800 : 650,
                    color: active ? portalChrome.brandRed : done ? portalChrome.ink : portalChrome.muted,
                  }}
                >
                  {row.label}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

CoreReturnProgressCard.propTypes = {
  lifecycle: PropTypes.object,
  t: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

export function MobilePortalCoreReturns() {
  const navigate = useNavigate();
  const { t } = useMobileLocale();
  const session = usePortalSession();
  const query = useQuery({
    queryKey: ["m-portal-core-returns-hub"],
    queryFn: () => get("/api/storefront/valve-bodies/me"),
    enabled: !!session.token && session.isCustomer,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const owedRows = useMemo(() => {
    const all = Array.isArray(query.data?.data) ? query.data.data : [];
    // Only real invoice-backed core obligations — never orphan/test returns.
    return all.filter((row) => {
      const lifecycle = row?.lifecycle || {};
      const outstanding = Number(
        lifecycle?.refundableCoreOutstandingQty ??
          lifecycle?.coreObligation?.outstandingQty ??
          0
      );
      return !!lifecycle?.coreDue && outstanding > 0;
    });
  }, [query.data]);

  return (
    <Guard customerOnly>
      <Shell title={t("Core returns")} subtitle={t("Return owed cores in three steps.")}>
        <LoadingOrError
          query={query}
          emptyText={t("You do not have any cores owed or active returns.")}
          hasRows={owedRows.length}
        />
        {owedRows.length ? (
          <AccountSection title={t("Cores you owe")}>
            {owedRows.map((unit) => {
              const lifecycle = unit.lifecycle || {};
              const request = lifecycle.activeReturnRequest || {};
              const statusLabel = coreReturnLifecycleLabel(lifecycle, (key, fallback) => t(fallback || key));
              return (
                <Box key={String(unit.documentId || unit.id)} sx={{ mb: 1.15 }}>
                  <AccountRecordCard
                    title={unit.partNumber || unit.displayTag || t("Valve Body")}
                    meta={
                      [request.shippingCarrier, request.trackingNumber].filter(Boolean).join(" · ") ||
                      (unit.tag ? `${t("Tag")} ${unit.tag}` : unit.serialNumber || t("Serial pending"))
                    }
                    chips={[
                      money(lifecycle.refundableCoreAmount, lifecycle.currency),
                      statusLabel,
                      lifecycle.coreDueAt ? `${t("Deadline")}: ${dateText(lifecycle.coreDueAt)}` : t("Return core"),
                    ]}
                    onClick={() => {
                      if (unit.documentId) navigate(`/m/account/valve-bodies/${unit.documentId}/core-return`);
                    }}
                  />
                  {request ? (
                    <Box sx={{ mt: 0.85 }}>
                      <CoreReturnProgressCard lifecycle={lifecycle} t={t} compact />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </AccountSection>
        ) : null}
      </Shell>
    </Guard>
  );
}

export function MobilePortalCoreReturn() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useMobileLocale();
  const session = usePortalSession();
  const [step, setStep] = useState("confirm");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [checklist, setChecklist] = useState({
    isNotLeakingOil: false,
    isInPlasticBag: false,
    isPackedInOldBox: false,
  });
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const detailQuery = useQuery({
    queryKey: ["m-portal-core", documentId],
    queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`),
    enabled: !!session.token && session.isCustomer && !!documentId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const lifecycle = detailQuery.data?.data?.lifecycle || {};
  const activeRequest = lifecycle.activeReturnRequest || null;
  const hasLabel = !!String(activeRequest?.shippingLabelUrl || "").trim();
  const providerFailed =
    String(activeRequest?.shippingProviderStatus || "")
      .trim()
      .toLowerCase() === "failed";
  const statusLabel = coreReturnLifecycleLabel(lifecycle, (key, fallback) => t(fallback || key));
  const checklistComplete =
    checklist.isNotLeakingOil && checklist.isInPlasticBag && checklist.isPackedInOldBox;
  const selectedOption = shippingOptions.find((option) => coreOptionKey(option) === selectedOptionKey);
  const steps = ["confirm", "pack", "ship"];
  const stepIndex = Math.max(0, steps.indexOf(step));

  useEffect(() => {
    if (!detailQuery.data) return;
    if (hasLabel || activeRequest) setStep("ship");
  }, [detailQuery.data, hasLabel, activeRequest]);

  const loadShippingOptions = async () => {
    if (!documentId) return;
    setOptionsLoading(true);
    setNotice(null);
    try {
      const res = await post(`/api/storefront/valve-bodies/${documentId}/core-return/shipping/options`, {
        data: { bookingMode: "label_only" },
      });
      const options = Array.isArray(res?.data?.shipping?.options) ? res.data.shipping.options : [];
      setShippingOptions(options);
      if (options.length) {
        setSelectedOptionKey((prev) => {
          if (prev && options.some((option) => coreOptionKey(option) === prev)) return prev;
          return coreOptionKey(options[0]);
        });
      }
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          t("Unable to load prepaid shipping options."),
      });
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (step !== "ship" || !activeRequest || hasLabel) return;
    loadShippingOptions();
  }, [step, !!activeRequest, hasLabel, documentId]);

  const onStartReturn = async () => {
    if (!checklistComplete) {
      setNotice({ type: "warning", text: t("Confirm the packaging checklist before continuing.") });
      return;
    }
    try {
      setSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(files);
      await post(`/api/storefront/valve-bodies/${documentId}/core-return`, {
        data: {
          notes,
          imageIds,
          conditionChecklist: checklist,
          bookShipping: false,
          bookingMode: "label_only",
        },
      });
      setFiles([]);
      await detailQuery.refetch();
      setStep("ship");
      setNotice({
        type: "success",
        text: t("Packaging confirmed. Choose where to drop off your core."),
      });
    } catch (error) {
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || t("Unable to start core return."),
      });
    } finally {
      setSaving(false);
    }
  };

  const onPrintLabel = async () => {
    if (!selectedOption) return;
    try {
      setSaving(true);
      setNotice(null);
      const res = await post(`/api/storefront/valve-bodies/${documentId}/core-return/shipping/book`, {
        data: {
          quoteId: selectedOption.quoteId,
          carrier: selectedOption.carrier,
          service: selectedOption.service,
          bookingMode: selectedOption.bookingMode || "label_only",
          force: providerFailed || !hasLabel,
        },
      });
      const labelUrl =
        String(res?.data?.shipping?.shippingLabelUrl || "").trim() ||
        String(res?.data?.returnRequest?.shippingLabelUrl || "").trim();
      await detailQuery.refetch();
      if (labelUrl) window.open(labelUrl, "_blank", "noopener,noreferrer");
      setNotice({ type: "success", text: t("Your prepaid return label is ready.") });
    } catch (error) {
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || t("Unable to generate the return label."),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Guard customerOnly>
      <Shell title={t("Core return")} subtitle={t("Confirm, pack, then print your prepaid label.")}>
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 0.75, mb: 1.15 }}>
          {steps.map((key, index) => {
            const labels = {
              confirm: t("1. Confirm"),
              pack: t("2. Pack"),
              ship: t("3. Label"),
            };
            const active = step === key;
            const complete = index < stepIndex || (key === "ship" && hasLabel);
            return (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  py: 0.7,
                  borderRadius: "10px",
                  bgcolor: active || complete ? portalChrome.brandRedSoft : portalChrome.surface,
                  border: `1px solid ${active || complete ? "rgba(224,24,32,0.28)" : portalChrome.line}`,
                  color: active || complete ? portalChrome.brandRed : portalChrome.muted,
                  fontSize: 11.5,
                  fontWeight: 800,
                }}
              >
                {labels[key]}
              </Box>
            );
          })}
        </Box>

        <AccountSection>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Box>
              <Typography sx={{ color: portalChrome.subtle, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.7, textTransform: "uppercase" }}>
                {t("Refundable core credit")}
              </Typography>
              <Typography sx={{ fontSize: 32, lineHeight: 1.05, fontWeight: 820, letterSpacing: -0.8, color: portalChrome.ink, mt: 0.5 }}>
                {money(lifecycle.refundableCoreAmount, lifecycle.currency)}
              </Typography>
            </Box>
            {(activeRequest || lifecycle.coreDue) && (
              <Box
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: "999px",
                  bgcolor: portalChrome.brandRedSoft,
                  color: portalChrome.brandRed,
                  fontSize: 11.5,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                {statusLabel}
              </Box>
            )}
          </Box>
          <Typography sx={{ mt: 0.75, color: portalChrome.muted, fontSize: 13, fontWeight: 650 }}>
            {t("Deadline")}: {dateText(lifecycle.coreDueAt)}
          </Typography>
        </AccountSection>

        {activeRequest ? <CoreReturnProgressCard lifecycle={lifecycle} t={t} /> : null}

        {step === "confirm" ? (
          <AccountSection title={t("Confirm this core")}>
            <Typography sx={{ color: portalChrome.muted, fontSize: 13.5, fontWeight: 650, mb: 1.25 }}>
              {t("You are returning this valve body core with a prepaid VBS label.")}
            </Typography>
            <MDButton
              fullWidth
              variant="contained"
              color="error"
              sx={{ ...accountPrimaryBtnSx, mb: 1 }}
              disabled={!lifecycle.coreDue}
              onClick={() => setStep("pack")}
            >
              {t("Return core")}
            </MDButton>
            <MDButton
              fullWidth
              variant="outlined"
              color="secondary"
              sx={accountSecondaryBtnSx}
              onClick={() => navigate("/m/account/core-returns")}
            >
              {t("Back")}
            </MDButton>
          </AccountSection>
        ) : null}

        {step === "pack" ? (
          <>
            <AccountSection title={t("Pack the core")}>
              {[
                [t("Drain all oil"), t("Drain the core completely. Wipe residue so the box is not dripping or leaking oil.")],
                [t("Seal in a plastic bag"), t("Place the core in the provided plastic bag and seal it before boxing.")],
                [t("Use the original box"), t("Pack the bagged core in the original or old box so it ships securely.")],
              ].map(([title, body]) => (
                <Box key={title} sx={{ mb: 1.1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: portalChrome.ink }}>{title}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 650, color: portalChrome.muted, mt: 0.25 }}>{body}</Typography>
                </Box>
              ))}
            </AccountSection>
            <AccountSection title={t("Packaging checklist")}>
              {[
                ["isNotLeakingOil", "The box is not leaking or dripping oil."],
                ["isInPlasticBag", "Core is sealed in a plastic bag."],
                ["isPackedInOldBox", "Core is packed in the original box."],
              ].map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  sx={{ display: "flex", ml: 0, mb: 0.35, alignItems: "flex-start" }}
                  control={
                    <Checkbox
                      checked={Boolean(checklist[key])}
                      onChange={(event) => setChecklist((prev) => ({ ...prev, [key]: event.target.checked }))}
                      sx={{ color: portalChrome.iconInk, "&.Mui-checked": { color: portalChrome.brandRed } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 13.5, fontWeight: 650, pt: 1 }}>{t(label)}</Typography>}
                />
              ))}
            </AccountSection>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t("Notes")}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              sx={{ ...accountFieldSx, mb: 1 }}
            />
            <MDButton component="label" fullWidth variant="outlined" color="secondary" sx={{ ...accountSecondaryBtnSx, mb: 1 }}>
              {t("Add photos")}
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => setFiles(Array.from(event.target.files || []))}
              />
            </MDButton>
            {files.length ? (
              <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mb: 1, fontWeight: 600 }}>
                {files.length} {files.length === 1 ? t("image selected") : t("images selected")}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <MDButton fullWidth variant="outlined" color="secondary" sx={accountSecondaryBtnSx} disabled={saving} onClick={() => setStep("confirm")}>
                {t("Back")}
              </MDButton>
              <MDButton
                fullWidth
                variant="contained"
                color="error"
                sx={accountPrimaryBtnSx}
                disabled={saving || !checklistComplete}
                onClick={onStartReturn}
              >
                {saving ? t("Saving...") : t("Continue")}
              </MDButton>
            </Stack>
          </>
        ) : null}

        {step === "ship" ? (
          <AccountSection title={hasLabel ? t("Your label is ready") : t("Choose drop-off option")}>
            {hasLabel ? (
              <>
                <Box
                  sx={{
                    mb: 1.15,
                    p: 1.25,
                    borderRadius: "14px",
                    bgcolor: "rgba(20,122,92,0.08)",
                    border: "1px solid rgba(20,122,92,0.18)",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: COLORS.green, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    {statusLabel}
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: portalChrome.ink, mt: 0.35 }}>
                    {[activeRequest?.shippingCarrier, activeRequest?.trackingNumber].filter(Boolean).join(" · ") || t("Label ready")}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: portalChrome.muted, mt: 0.35 }}>
                    {t("Staff ERP tracks this return with the same status.")}
                  </Typography>
                </Box>
                <MDButton
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  sx={{ ...accountSecondaryBtnSx, mb: 1 }}
                  onClick={() => navigate("/m/account/core-returns")}
                >
                  {t("Back")}
                </MDButton>
                <MDButton
                  fullWidth
                  variant="contained"
                  color="error"
                  sx={{ ...accountPrimaryBtnSx, mb: 1 }}
                  onClick={() => window.open(activeRequest.shippingLabelUrl, "_blank", "noopener,noreferrer")}
                >
                  {t("Print label")}
                </MDButton>
                {activeRequest?.shippingProviderTrackUrl ? (
                  <MDButton
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    sx={accountSecondaryBtnSx}
                    onClick={() =>
                      window.open(activeRequest.shippingProviderTrackUrl, "_blank", "noopener,noreferrer")
                    }
                  >
                    {t("Track return")}
                  </MDButton>
                ) : null}
              </>
            ) : (
              <>
                <Typography sx={{ color: portalChrome.muted, fontSize: 13, fontWeight: 650, mb: 1.1 }}>
                  {t("Prepaid options — no charge to you.")}
                </Typography>
                {optionsLoading ? (
                  <Box sx={{ py: 2, display: "grid", placeItems: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : shippingOptions.length ? (
                  shippingOptions.map((option) => {
                    const key = coreOptionKey(option);
                    const selected = selectedOptionKey === key;
                    const carrierLabel = option.carrierDescription || option.carrier || "";
                    const carrierKey = String(option.carrier || "")
                      .trim()
                      .toLowerCase()
                      .replace(/\s+/g, "");
                    const modeLabel =
                      option.bookingMode === "pickup_plus_label"
                        ? t("Pickup + prepaid label")
                        : t("Print label and drop off");
                    const dropOffLocations = (
                      Array.isArray(option.dropOffLocations) ? option.dropOffLocations : []
                    ).filter((location) => {
                      const locationCarrier = String(location?.carrier || "")
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, "");
                      return !locationCarrier || !carrierKey || locationCarrier === carrierKey;
                    });
                    return (
                      <Box key={key} sx={{ mb: 1 }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedOptionKey(key)}
                          sx={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            p: 1.25,
                            borderRadius: "12px",
                            border: `1.5px solid ${selected ? portalChrome.brandRed : portalChrome.line}`,
                            bgcolor: selected ? portalChrome.brandRedSoft : portalChrome.surface,
                            cursor: "pointer",
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: portalChrome.ink }}>
                            {carrierLabel} · {option.serviceDescription || option.service}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: portalChrome.muted, mt: 0.25 }}>
                            {modeLabel}
                            {option.deliveryDays != null ? ` · ${option.deliveryDays} ${t("day estimate")}` : ""}
                          </Typography>
                        </Box>
                        {selected ? (
                          <Box sx={{ mt: 0.85, mb: 0.25 }}>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: portalChrome.subtle,
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                mb: 0.75,
                              }}
                            >
                              {t("{carrier} drop-off locations").replace(
                                "{carrier}",
                                carrierLabel || t("Nearby drop-off locations")
                              )}
                            </Typography>
                            {dropOffLocations.length ? (
                              dropOffLocations.slice(0, 6).map((location) => (
                                <Box
                                  key={location.id || location.code || location.addressText || location.name}
                                  sx={{
                                    mb: 0.85,
                                    p: 1.1,
                                    borderRadius: "12px",
                                    border: `1px solid ${portalChrome.line}`,
                                    bgcolor: portalChrome.surface,
                                  }}
                                >
                                  <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: portalChrome.ink }}>
                                    {location.name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: portalChrome.muted, mt: 0.2 }}>
                                    {location.addressText ||
                                      [location.street, location.city].filter(Boolean).join(", ")}
                                  </Typography>
                                  {location.mapsUrl ? (
                                    <MDButton
                                      size="small"
                                      variant="text"
                                      color="secondary"
                                      sx={{ mt: 0.35, px: 0, minWidth: 0, textTransform: "none", fontWeight: 750 }}
                                      onClick={() =>
                                        window.open(location.mapsUrl, "_blank", "noopener,noreferrer")
                                      }
                                    >
                                      {t("Open map")}
                                    </MDButton>
                                  ) : null}
                                </Box>
                              ))
                            ) : (
                              <Alert severity="info" sx={{ borderRadius: "14px" }}>
                                {t(
                                  "No nearby drop-off locations were returned for this carrier. Use the carrier website locator after printing your label."
                                )}
                              </Alert>
                            )}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })
                ) : (
                  <Alert severity="warning" sx={{ borderRadius: "14px", mb: 1 }}>
                    {t("No prepaid drop-off options are available right now.")}
                  </Alert>
                )}
                <MDButton
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  sx={{ ...accountSecondaryBtnSx, mb: 1 }}
                  disabled={saving}
                  onClick={() => navigate("/m/account/core-returns")}
                >
                  {t("Back")}
                </MDButton>
                <MDButton
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  sx={{ ...accountSecondaryBtnSx, mb: 1 }}
                  disabled={saving || optionsLoading}
                  onClick={loadShippingOptions}
                >
                  {t("Refresh options")}
                </MDButton>
                <MDButton
                  fullWidth
                  variant="contained"
                  color="error"
                  sx={accountPrimaryBtnSx}
                  disabled={saving || optionsLoading || !selectedOption}
                  onClick={onPrintLabel}
                >
                  {saving ? t("Generating label...") : t("Print label")}
                </MDButton>
              </>
            )}
          </AccountSection>
        ) : null}
      </Shell>
    </Guard>
  );
}

export function MobilePortalWarrantyCreate() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const detailQuery = useQuery({ queryKey: ["m-portal-warranty-source", documentId], queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`), enabled: !!session.token && session.isCustomer && !!documentId, staleTime: 15000 });
  const activeWarranty = detailQuery.data?.data?.lifecycle?.activeWarranty || null;
  const [form, setForm] = useState({ vehicleYear: "", vehicleMake: "", vehicleModel: "", vehicleTrim: "", vehicleVin: "", issueCodes: "", issueDescription: "", notes: "" });
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const validationMessage = (() => {
    if ((Number(form.vehicleYear) || 0) <= 0) return t("Vehicle year is required.");
    if (!form.vehicleMake.trim()) return t("Vehicle make is required.");
    if (!form.vehicleModel.trim()) return t("Vehicle model is required.");
    if (!form.issueDescription.trim()) return t("Problem description is required.");
    return "";
  })();
  const submit = async () => {
    if (validationMessage) {
      setNotice({ type: "warning", text: validationMessage });
      return;
    }
    try {
      setSaving(true);
      setNotice(null);
      const res = await post(`/api/storefront/valve-bodies/${documentId}/warranty-claims`, {
        data: {
          ...form,
          vehicleYear: Number(form.vehicleYear || 0),
          issueCodes: form.issueCodes.split(",").map((v) => v.trim()).filter(Boolean),
        },
      });
      const claim = res?.data?.claim;
      if (claim?.documentId) navigate(`/m/account/warranty-claims/${claim.documentId}`, { replace: true });
      else setNotice({ type: "success", text: t("Warranty claim created.") });
    } catch (error) {
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || t("Unable to create warranty claim."),
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Guard customerOnly>
      <Shell title="Warranty claim" subtitle="Open an RMA.">
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        {activeWarranty ? (
          <Alert severity="info" sx={{ borderRadius: "14px", mb: 1.15 }}>
            {t("Active RMA")}: {activeWarranty.rmaNumber || activeWarranty.documentId}.
          </Alert>
        ) : null}
        <AccountSection title="Vehicle">
          <Stack spacing={1}>
            <TextField label={t("Vehicle year")} value={form.vehicleYear} onChange={(e) => setField("vehicleYear", e.target.value)} sx={accountFieldSx} />
            <TextField label={t("Make")} value={form.vehicleMake} onChange={(e) => setField("vehicleMake", e.target.value)} sx={accountFieldSx} />
            <TextField label={t("Model")} value={form.vehicleModel} onChange={(e) => setField("vehicleModel", e.target.value)} sx={accountFieldSx} />
            <TextField label={t("Trim")} value={form.vehicleTrim} onChange={(e) => setField("vehicleTrim", e.target.value)} sx={accountFieldSx} />
            <TextField label="VIN" value={form.vehicleVin} onChange={(e) => setField("vehicleVin", e.target.value)} sx={accountFieldSx} />
          </Stack>
        </AccountSection>
        <AccountSection title="Issue">
          <Stack spacing={1}>
            <TextField label={t("Codes")} value={form.issueCodes} onChange={(e) => setField("issueCodes", e.target.value)} sx={accountFieldSx} />
            <TextField
              multiline
              minRows={5}
              label={t("Problem description")}
              value={form.issueDescription}
              onChange={(e) => setField("issueDescription", e.target.value)}
              sx={accountFieldSx}
            />
            <TextField
              multiline
              minRows={3}
              label={t("Additional notes")}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              sx={accountFieldSx}
            />
          </Stack>
        </AccountSection>
        {validationMessage ? (
          <Alert severity="warning" sx={{ borderRadius: "14px", mb: 1.15 }}>
            {validationMessage}
          </Alert>
        ) : null}
        <MDButton
          fullWidth
          variant="contained"
          color="error"
          sx={accountPrimaryBtnSx}
          disabled={saving || !!activeWarranty || !!validationMessage}
          onClick={submit}
        >
          {saving ? t("Submitting...") : t("Create RMA")}
        </MDButton>
      </Shell>
    </Guard>
  );
}

export function MobilePortalWarrantyClaim() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [updateMessage, setUpdateMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [shippingMode, setShippingMode] = useState("");
  const [shippingSaving, setShippingSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const query = useQuery({ queryKey: ["m-portal-claim", documentId], queryFn: () => get(`/api/storefront/warranty-claims/${documentId}`), enabled: !!session.token && session.isCustomer && !!documentId, staleTime: 10000 });
  const claim = query.data?.data?.claim || null;
  const shipping = query.data?.data?.shipping || {};
  const returnRequest = normalizeEntity(claim?.returnRequest);
  const replacementSalesOrder = normalizeEntity(claim?.replacementSalesOrder);
  const thread = normalizeEntity(claim?.supportThread);
  const updates = Array.isArray(claim?.updates) ? claim.updates : [];
  const activeShippingMode = String(claim?.requestedShippingBookingMode || shipping?.requestedShippingBookingMode || returnRequest?.shippingBookingMode || shipping?.config?.defaultBookingMode || "pickup_plus_label").trim().toLowerCase();
  const selectedShippingMode = shippingMode || activeShippingMode;
  const claimStatus = String(claim?.status || "").trim().toLowerCase();
  const canBookShippingNow = shipping?.canBookNow === true || ["approved", "received", "testing"].includes(claimStatus);
  const canRequestShipping = shipping?.canRequestShipping !== false && !canBookShippingNow;
  const handleShippingAction = async () => {
    try {
      setShippingSaving(true);
      setNotice(null);
      const endpoint = canBookShippingNow ? `/api/storefront/warranty-claims/${documentId}/shipping/rebook` : `/api/storefront/warranty-claims/${documentId}/shipping/request`;
      await post(endpoint, { data: { bookingMode: selectedShippingMode, force: true } });
      setNotice({
        type: "success",
        text: canBookShippingNow ? t("Shipping label updated.") : t("Shipping option requested."),
      });
      await query.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to update shipping.") });
    } finally {
      setShippingSaving(false);
    }
  };
  const sendUpdate = async () => {
    const message = updateMessage.trim();
    if (!message && !files.length) return;
    try {
      setSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(files);
      await post(`/api/storefront/warranty-claims/${documentId}/updates`, { data: { message, imageIds } });
      setUpdateMessage("");
      setFiles([]);
      setNotice({ type: "success", text: t("Update sent.") });
      await query.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to send update.") });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Guard customerOnly>
      <Shell title={claim?.rmaNumber || "Warranty claim"} subtitle="Status, shipping, and updates.">
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        <LoadingOrError query={query} emptyText="Warranty claim not found." hasRows={!!claim} />
        {claim ? (
          <>
            <AccountSection title="Claim">
              <AccountChipRow
                chips={[
                  { label: pretty(claim.workflowStage || claim.status), emphasis: true },
                  dateText(claim.submittedAt || claim.createdAt),
                ]}
              />
              <Box sx={{ mt: 0.5 }}>
                <DetailRow
                  label="Vehicle"
                  value={
                    [claim.vehicleYear, claim.vehicleMake, claim.vehicleModel, claim.vehicleTrim]
                      .filter(Boolean)
                      .join(" ") || "Pending"
                  }
                  first
                />
                <DetailRow label="VIN" value={claim.vehicleVin || "Pending"} />
                <DetailRow
                  label="Issue codes"
                  value={
                    Array.isArray(claim.issueCodes) && claim.issueCodes.length
                      ? claim.issueCodes.join(", ")
                      : "Pending"
                  }
                />
                <DetailRow label="Issue" value={claim.issueDescription || claim.description || "Pending"} />
              </Box>
            </AccountSection>

            <AccountSection title="Shipping and replacement">
              <DetailRow label="Provider" value={`${String(shipping?.provider || "envia").toUpperCase()} · ${pretty(activeShippingMode)}`} first />
              <DetailRow
                label="Carrier"
                value={returnRequest?.shippingCarrier || shipping?.config?.defaultCarrier || "Pending"}
              />
              <DetailRow
                label="Inbound tracking"
                value={
                  returnRequest?.trackingNumber ||
                  claim.inboundTrackingNumber ||
                  claim.returnTrackingNumber ||
                  "Pending"
                }
              />
              <DetailRow
                label="Status"
                value={pretty(returnRequest?.status || returnRequest?.shippingProviderStatus || "Requested")}
              />
              <DetailRow
                label="Replacement order"
                value={
                  replacementSalesOrder?.salesOrderNumber ||
                  claim.replacementOrder?.salesOrderNumber ||
                  claim.replacementOrderNumber ||
                  "Pending"
                }
              />
              <Stack direction="row" spacing={0.75} sx={{ mt: 1.15, flexWrap: "wrap", rowGap: 1 }}>
                {returnRequest?.shippingLabelUrl || claim.inboundLabelUrl ? (
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={accountSecondaryBtnSx}
                    onClick={() =>
                      window.open(
                        returnRequest?.shippingLabelUrl || claim.inboundLabelUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    {t("Open label")}
                  </MDButton>
                ) : null}
                {returnRequest?.shippingProviderTrackUrl ? (
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={accountSecondaryBtnSx}
                    onClick={() =>
                      window.open(returnRequest.shippingProviderTrackUrl, "_blank", "noopener,noreferrer")
                    }
                  >
                    {t("Track shipment")}
                  </MDButton>
                ) : null}
              </Stack>
              <Divider sx={{ my: 1.35 }} />
              <TextField
                select
                fullWidth
                size="small"
                label={t("Shipping option")}
                value={selectedShippingMode}
                onChange={(event) => setShippingMode(event.target.value)}
                disabled={!canBookShippingNow && !canRequestShipping}
                sx={accountFieldSx}
              >
                {(Array.isArray(shipping?.options) && shipping.options.length
                  ? shipping.options
                  : [
                      { bookingMode: "pickup_plus_label", label: "Pickup + label" },
                      { bookingMode: "label_only", label: "Label only" },
                    ]
                ).map((option) => (
                  <MenuItem key={String(option.bookingMode || "")} value={String(option.bookingMode || "")}>
                    {t(option.label || pretty(option.bookingMode))}
                  </MenuItem>
                ))}
              </TextField>
              {canBookShippingNow || canRequestShipping ? (
                <MDButton
                  fullWidth
                  sx={{ ...accountSecondaryBtnSx, mt: 1 }}
                  variant="outlined"
                  color="secondary"
                  disabled={shippingSaving}
                  onClick={handleShippingAction}
                >
                  {shippingSaving
                    ? t("Saving...")
                    : canBookShippingNow
                    ? t("Generate label")
                    : t("Request shipping")}
                </MDButton>
              ) : null}
            </AccountSection>

            <Box sx={{ mb: 1.15 }}>
              <Tile
                icon="chat"
                title="Claim chat"
                text="Message support and attach photos."
                onClick={() => navigate(`/m/account/warranty-claims/${documentId}/chat`)}
                tone="red"
              />
            </Box>
            {!thread ? (
              <Alert severity="info" sx={{ borderRadius: "14px", mb: 1.15 }}>
                {t("Chat thread is being prepared for this claim.")}
              </Alert>
            ) : null}

            <AccountSection title="Send an update">
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t("Update")}
                value={updateMessage}
                onChange={(event) => setUpdateMessage(event.target.value)}
                sx={accountFieldSx}
              />
              <MDButton
                component="label"
                fullWidth
                variant="outlined"
                color="secondary"
                sx={{ ...accountSecondaryBtnSx, mt: 1 }}
              >
                {t("Attach photos")}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => setFiles(Array.from(event.target.files || []))}
                />
              </MDButton>
              {files.length ? (
                <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 0.75, fontWeight: 600 }}>
                  {files.length} {files.length === 1 ? t("image selected") : t("images selected")}
                </Typography>
              ) : null}
              <MDButton
                fullWidth
                variant="contained"
                color="error"
                sx={{ ...accountPrimaryBtnSx, mt: 1 }}
                disabled={saving || (!updateMessage.trim() && !files.length)}
                onClick={sendUpdate}
              >
                {saving ? t("Sending...") : t("Send update")}
              </MDButton>
            </AccountSection>

            {updates.length ? (
              <>
                <Typography
                  sx={{
                    px: 0.5,
                    mb: 0.75,
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 0.9,
                    textTransform: "uppercase",
                    color: portalChrome.subtle,
                  }}
                >
                  {t("Updates")}
                </Typography>
                {updates.map((update, index) => (
                  <AccountSection key={String(update.documentId || update.id || index)} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 750, color: portalChrome.muted }}>
                      {t(pretty(update.authorRole || update.source || "Update"))} · {dateText(update.createdAt)}
                    </Typography>
                    <Typography sx={{ mt: 0.45, fontSize: 14, whiteSpace: "pre-wrap", fontWeight: 600 }}>
                      {update.message || update.notes || t("Update received.")}
                    </Typography>
                  </AccountSection>
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </Shell>
    </Guard>
  );
}

export function MobilePortalWarrantyChat() {
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const claimQuery = useQuery({ queryKey: ["m-portal-chat-claim", documentId], queryFn: () => get(`/api/storefront/warranty-claims/${documentId}`), enabled: !!session.token && session.isCustomer && !!documentId, staleTime: 10000 });
  const claim = claimQuery.data?.data?.claim || null;
  const supportThread = normalizeEntity(claim?.supportThread);
  const threadDocumentId = String(supportThread?.documentId || "").trim();
  const chatQuery = useQuery({ queryKey: ["m-portal-chat", threadDocumentId], queryFn: () => get(`/api/support-chat/threads/${threadDocumentId}`), enabled: !!session.token && session.isCustomer && !!threadDocumentId, staleTime: 5000 });
  const messages = useMemo(() => (Array.isArray(chatQuery.data?.data?.messages) ? chatQuery.data.data.messages : []), [chatQuery.data]);
  const send = async () => {
    const trimmed = message.trim();
    if (!threadDocumentId || (!trimmed && !files.length)) return;
    try {
      setSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(files);
      await post(`/api/support-chat/threads/${threadDocumentId}/messages`, {
        data: {
          message:
            trimmed ||
            `${t("Uploaded")} ${imageIds.length} ${imageIds.length === 1 ? t("image") : t("images")}.`,
          imageIds,
        },
      });
      setMessage("");
      setFiles([]);
      await chatQuery.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to send message.") });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Guard customerOnly>
      <Shell
        title={claim?.rmaNumber ? `${claim.rmaNumber} · ${t("chat")}` : "Claim chat"}
        subtitle="Support messages."
      >
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        {!threadDocumentId && !claimQuery.isLoading ? (
          <Alert severity="info" sx={{ borderRadius: "14px", mb: 1.15 }}>
            {t("Chat thread is being prepared.")}
          </Alert>
        ) : null}
        <Stack spacing={0.85} sx={{ mb: 1.5 }}>
          {messages.map((row, index) => {
            const fromStaff = row.senderRole === "staff";
            return (
              <Box
                key={String(row.documentId || index)}
                sx={{
                  alignSelf: fromStaff ? "flex-start" : "flex-end",
                  maxWidth: "92%",
                  bgcolor: fromStaff ? portalChrome.surface : portalChrome.iconInk,
                  color: fromStaff ? portalChrome.ink : "#fff",
                  border: `1px solid ${fromStaff ? portalChrome.line : "transparent"}`,
                  borderRadius: "16px",
                  p: 1.25,
                  boxShadow: portalChrome.lift,
                }}
              >
                <Typography sx={{ fontSize: 11.5, fontWeight: 750, opacity: 0.75 }}>
                  {fromStaff ? t("VBS Support") : t("You")} · {dateText(row.createdAt)}
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: 14, whiteSpace: "pre-wrap", fontWeight: 600 }}>
                  {row.message}
                </Typography>
                {Array.isArray(row.imageUrls) && row.imageUrls.length ? (
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
                    {row.imageUrls.map((url, imageIndex) => (
                      <MDButton
                        key={`${url}-${imageIndex}`}
                        size="small"
                        variant={fromStaff ? "outlined" : "text"}
                        color="secondary"
                        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                        sx={fromStaff ? accountSecondaryBtnSx : { color: "#fff", fontWeight: 750 }}
                      >
                        {t("Photo")} {imageIndex + 1}
                      </MDButton>
                    ))}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
        </Stack>
        <AccountSection title="Compose">
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t("Message")}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            sx={accountFieldSx}
          />
          <MDButton
            component="label"
            fullWidth
            variant="outlined"
            color="secondary"
            sx={{ ...accountSecondaryBtnSx, mt: 1 }}
          >
            {t("Attach photos")}
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
            />
          </MDButton>
          {files.length ? (
            <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 0.75, fontWeight: 600 }}>
              {files.length} {files.length === 1 ? t("image selected") : t("images selected")}
            </Typography>
          ) : null}
          <MDButton
            fullWidth
            variant="contained"
            color="error"
            sx={{ ...accountPrimaryBtnSx, mt: 1 }}
            disabled={saving || !threadDocumentId}
            onClick={send}
          >
            {saving ? t("Sending...") : t("Send")}
          </MDButton>
        </AccountSection>
      </Shell>
    </Guard>
  );
}

function requestTotal(request) {
  const totals = request?.totalsSnapshot && typeof request.totalsSnapshot === "object" ? request.totalsSnapshot : {};
  return money(totals.total ?? totals.grandTotal ?? totals.orderTotal ?? totals.subtotal, totals.currency || "USD");
}

function requestAddressText(address) {
  if (!address || typeof address !== "object") return "Address pending";
  return [address.name, address.line1, address.line2, address.city, address.region || address.state, address.postalCode, address.country]
    .filter(Boolean)
    .join(", ");
}

export function MobilePortalDistributorRequests() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [status, setStatus] = useState("");
  const query = useQuery({
    queryKey: ["m-distributor-requests", status],
    queryFn: () =>
      get("/api/storefront/distributor-orders/requests", {
        pagination: { page: 1, pageSize: 50 },
        ...(status ? { status } : {}),
      }),
    enabled: !!session.token && session.isDistributor,
    staleTime: 10000,
  });
  const rows = Array.isArray(query.data?.data) ? query.data.data : [];
  const filters = [
    { key: "", label: t("All"), icon: "filter_list" },
    { key: "submitted", label: t("Submitted"), icon: "inbox", accent: "orange" },
    { key: "under_review", label: t("Review"), icon: "manage_search" },
    { key: "fulfilled_by_distributor", label: t("Fulfilled"), icon: "check_circle", accent: "green" },
    { key: "redirected_to_warehouse", label: t("Redirected"), icon: "warehouse", accent: "red" },
  ];
  return (
    <Guard distributorOnly>
      <Shell title="Order requests" subtitle="Customer requests for your account.">
        <Box sx={{ mb: 1.15 }}>
          <AccountFilterBar label={t("Status")} value={status} options={filters} onChange={setStatus} />
        </Box>
        <LoadingOrError query={query} emptyText="No distributor order requests yet." hasRows={rows.length} />
        {rows.map((request) => (
          <RowCard
            key={String(request.documentId)}
            title={request.requestingCustomer?.name || t("Customer request")}
            meta={`${dateText(request.requestedAt || request.createdAt)} · ${request.lineCount || 0} item${
              Number(request.lineCount || 0) === 1 ? "" : "s"
            }`}
            chips={[pretty(request.status), requestTotal(request)]}
            onClick={() => navigate(`/m/account/distributor-requests/${request.documentId}`)}
          />
        ))}
      </Shell>
    </Guard>
  );
}

export function MobilePortalDistributorRequestDetail() {
  const { documentId } = useParams();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const query = useQuery({
    queryKey: ["m-distributor-request", documentId],
    queryFn: () => get(`/api/storefront/distributor-orders/requests/${documentId}`),
    enabled: !!session.token && session.isDistributor && !!documentId,
    staleTime: 10000,
  });
  const request = query.data?.data || null;
  const lines = Array.isArray(request?.lines) ? request.lines : [];
  const totals = request?.totalsSnapshot && typeof request.totalsSnapshot === "object" ? request.totalsSnapshot : {};
  const [decisionReason, setDecisionReason] = useState("");
  const [savingDecision, setSavingDecision] = useState("");
  const [notice, setNotice] = useState(null);
  const status = String(request?.status || "").toLowerCase();
  const canDecide = status === "submitted" || status === "under_review";

  const decide = async (decisionType) => {
    try {
      setSavingDecision(decisionType);
      setNotice(null);
      await post(`/api/storefront/distributor-orders/requests/${documentId}/decision`, {
        data: { decisionType, decisionReason: decisionReason.trim() || null },
      });
      setNotice({ type: "success", text: decisionType === "redirect_warehouse" ? t("Request sent to VBS.") : t("Request marked fulfilled.") });
      await query.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to update request.") });
    } finally {
      setSavingDecision("");
    }
  };

  return (
    <Guard distributorOnly>
      <Shell title="Order request" subtitle={request?.requestingCustomer?.name || "Customer request"}>
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        <LoadingOrError query={query} emptyText="Order request not found." hasRows={!!request} />
        {request ? (
          <>
            <AccountSection title="Request">
              <AccountChipRow
                chips={[
                  { label: pretty(request.status), emphasis: canDecide },
                  requestTotal(request),
                  `${lines.length} item${lines.length === 1 ? "" : "s"}`,
                ]}
              />
              <Box sx={{ mt: 0.5 }}>
                <DetailRow label="Customer" value={request.requestingCustomer?.name || "Pending"} first />
                <DetailRow label="Requested" value={dateText(request.requestedAt || request.createdAt)} />
                <DetailRow label="Decision" value={pretty(request.decisionType || "Pending")} />
                {request.redirectedSalesOrder ? (
                  <DetailRow
                    label="VBS order"
                    value={request.redirectedSalesOrder.salesOrderNumber || request.redirectedSalesOrder.documentId}
                  />
                ) : null}
              </Box>
            </AccountSection>
            <AccountSection title="Shipping">
              <Typography sx={{ color: portalChrome.muted, fontSize: 13.5, lineHeight: 1.5, fontWeight: 600 }}>
                {requestAddressText(request.shipToSnapshot)}
              </Typography>
            </AccountSection>
            {lines.length ? (
              <AccountListGroup title="Line items">
                {lines.map((line, index) => {
                  const itemName =
                    line.name || line.productName || line.inventoryName || line.inventoryItem?.name || "Item";
                  const sku = line.sku || line.inventorySku || line.inventoryItem?.sku || "";
                  const quantity = Number(line.quantity || line.qty || 1);
                  const unitPrice = line.unitPrice ?? line.price ?? line.sellPrice ?? 0;
                  const coreCharge = line.coreCharge ?? line.coreDeposit ?? 0;
                  return (
                    <Box
                      key={String(line.documentId || line.id || index)}
                      sx={{
                        px: 1.35,
                        py: 1.2,
                        borderBottom: index < lines.length - 1 ? `1px solid ${portalChrome.line}` : "none",
                      }}
                    >
                      <Typography sx={{ fontWeight: 750, fontSize: 14.5 }}>
                        {sku ? `${sku} ` : ""}
                        {itemName}
                      </Typography>
                      <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 0.3, fontWeight: 600 }}>
                        {t("Qty")} {quantity} · {t("Unit")} {money(unitPrice, totals.currency)} · {t("Core")}{" "}
                        {money(coreCharge, totals.currency)}
                      </Typography>
                    </Box>
                  );
                })}
              </AccountListGroup>
            ) : null}
            <AccountSection title="Totals">
              <DetailRow label="Subtotal" value={money(totals.subtotal, totals.currency)} first />
              <DetailRow label="Core" value={money(totals.coreTotal || totals.coreChargeTotal, totals.currency)} />
              <DetailRow label="Shipping" value={money(totals.shippingAmount || totals.shipping, totals.currency)} />
              <DetailRow label="Total" value={requestTotal(request)} />
            </AccountSection>
            {canDecide ? (
              <AccountSection title="Distributor decision">
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("Notes")}
                  value={decisionReason}
                  onChange={(event) => setDecisionReason(event.target.value)}
                  sx={{ ...accountFieldSx, mb: 1 }}
                />
                <Stack spacing={0.75}>
                  <MDButton
                    fullWidth
                    variant="contained"
                    color="error"
                    sx={accountPrimaryBtnSx}
                    disabled={!!savingDecision}
                    onClick={() => decide("fulfill_self")}
                  >
                    {savingDecision === "fulfill_self" ? t("Saving...") : t("Fulfill from distributor")}
                  </MDButton>
                  <MDButton
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    sx={accountSecondaryBtnSx}
                    disabled={!!savingDecision}
                    onClick={() => decide("redirect_warehouse")}
                  >
                    {savingDecision === "redirect_warehouse" ? t("Sending...") : t("Send to VBS warehouse")}
                  </MDButton>
                </Stack>
              </AccountSection>
            ) : null}
          </>
        ) : null}
      </Shell>
    </Guard>
  );
}

export function MobilePortalDistributorCustomers() {
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const query = useQuery({
    queryKey: ["m-distributor-customers"],
    queryFn: () => get("/api/storefront/distributor/customers"),
    enabled: !!session.token && session.isDistributor,
    staleTime: 15000,
  });
  const rows = Array.isArray(query.data?.data) ? query.data.data : [];
  return (
    <Guard distributorOnly>
      <Shell title="Linked customers" subtitle="Assigned to your account.">
        <LoadingOrError query={query} emptyText="No linked customers found." hasRows={rows.length} />
        {rows.map((customer) => (
          <AccountSection key={String(customer.documentId)}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: -0.2 }}>
              {customer.name || t("Customer")}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <AccountChipRow
                chips={[
                  pretty(customer.customerKind || "customer"),
                  pretty(customer.priceTier || "retail"),
                  pretty(customer.linkedVia || "linked"),
                ]}
              />
            </Box>
            <Typography sx={{ color: portalChrome.muted, fontSize: 12.5, mt: 1, fontWeight: 600 }}>
              {t("Linked")}: {dateText(customer.linkedAt)}
            </Typography>
          </AccountSection>
        ))}
      </Shell>
    </Guard>
  );
}

export function MobilePortalAddresses() {
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const [addresses, setAddresses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const lookup = useStorefrontAddressLookup();
  const { syncRows } = lookup;
  const query = useQuery({
    queryKey: ["m-profile-addresses"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!session.token && session.isPortal,
    staleTime: 10000,
  });

  useEffect(() => {
    const rows = Array.isArray(query.data?.profile?.addresses) ? query.data.profile.addresses : [];
    setAddresses(rows.map((row) => normalizeAddressRow(row)));
  }, [query.data]);

  useEffect(() => {
    syncRows(addresses, addressRowKey);
  }, [addresses, syncRows]);

  const updateAddress = (index, field, value) => {
    setAddresses((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === "formattedAddress" || field === "street" || field === "city" || field === "state" || field === "postalCode" || field === "country"
                ? { placeId: field === "formattedAddress" ? "" : row.placeId }
                : {}),
            }
          : row
      )
    );
  };

  const applySelectedCandidate = (index, candidate) => {
    const rowKey = addressRowKey(addresses[index], index);
    lookup.applyCandidateForRow(rowKey, candidate);
    setAddresses((prev) => prev.map((row, rowIndex) => (rowIndex === index ? applyAddressCandidate(row, candidate) : row)));
  };

  const save = async () => {
    try {
      setSaving(true);
      setNotice(null);
      const next = addresses.map((row) => stripAddressRowForSave(row)).filter((row) => isAddressRowFilled(row));
      if (next.some((row) => !isVerifiedAddressRow(row))) {
        throw new Error(t("Select a verified address from suggestions for every saved address."));
      }
      const primaryAddress = next.find((row) => row.isPrimary) || next[0] || null;
      await put("/api/storefront/me", {
        data: {
          addresses: next,
          ...(primaryAddress?.addressId ? { primaryAddressId: String(primaryAddress.addressId) } : {}),
        },
      });
      setNotice({ type: "success", text: t("Addresses saved.") });
      await query.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to save addresses.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Guard>
      <Shell
        title="Addresses"
        subtitle="Saved for checkout."
        action={
          <Stack direction="row" spacing={0.75}>
            <MDButton
              fullWidth
              variant="outlined"
              color="secondary"
              sx={accountSecondaryBtnSx}
              disabled={saving}
              onClick={() => setAddresses((prev) => [...prev, createAddressRow(null, { isPrimary: !prev.length })])}
            >
              {t("Add")}
            </MDButton>
            <MDButton
              fullWidth
              variant="contained"
              color="error"
              sx={accountPrimaryBtnSx}
              disabled={saving || query.isLoading}
              onClick={save}
            >
              {saving ? t("Saving...") : t("Save")}
            </MDButton>
          </Stack>
        }
      >
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        <LoadingOrError
          query={query}
          emptyText="No saved addresses yet."
          hasRows={query.isLoading || addresses.length}
        />
        {addresses.map((address, index) => (
          <AccountSection key={addressRowKey(address, index)}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                <Typography sx={{ fontWeight: 800, fontSize: 15.5 }}>
                  {address.label || `${t("Address")} ${index + 1}`}
                </Typography>
                {address.isPrimary ? (
                  <AccountChipRow chips={[{ label: t("Primary"), emphasis: true }]} />
                ) : null}
              </Box>
              <TextField
                size="small"
                label={t("Label")}
                value={address.label || ""}
                disabled={saving}
                onChange={(event) => updateAddress(index, "label", event.target.value)}
                sx={accountFieldSx}
              />
              <TextField
                size="small"
                label={t("Recipient name")}
                value={address.name || ""}
                disabled={saving}
                onChange={(event) => updateAddress(index, "name", event.target.value)}
                sx={accountFieldSx}
              />
              <VerifiedAddressSearch
                rowKey={addressRowKey(address, index)}
                address={address}
                lookup={lookup}
                disabled={saving}
                label={t("Search address")}
                helperText={t("Choose the exact place from suggestions.")}
                onTyping={() => updateAddress(index, "formattedAddress", "")}
                onSelect={(option) => applySelectedCandidate(index, option)}
              />
              <Stack direction="row" spacing={0.75}>
                <TextField
                  size="small"
                  label={t("Email")}
                  value={address.email || ""}
                  disabled={saving}
                  onChange={(event) => updateAddress(index, "email", event.target.value)}
                  sx={accountFieldSx}
                />
                <TextField
                  size="small"
                  label={t("Phone")}
                  value={address.phone || ""}
                  disabled={saving}
                  onChange={(event) => updateAddress(index, "phone", event.target.value)}
                  sx={accountFieldSx}
                />
              </Stack>
              <Stack direction="row" spacing={0.75}>
                {!address.isPrimary ? (
                  <MDButton
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    sx={accountSecondaryBtnSx}
                    disabled={saving}
                    onClick={() =>
                      setAddresses((prev) => prev.map((row, rowIndex) => ({ ...row, isPrimary: rowIndex === index })))
                    }
                  >
                    {t("Set primary")}
                  </MDButton>
                ) : null}
                <MDButton
                  fullWidth
                  variant="text"
                  color="error"
                  disabled={saving}
                  sx={{ borderRadius: "12px", fontWeight: 750, color: portalChrome.danger, textTransform: "none" }}
                  onClick={() => {
                    lookup.clearRow(addressRowKey(address, index));
                    setAddresses((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
                  }}
                >
                  {t("Remove")}
                </MDButton>
              </Stack>
            </Stack>
          </AccountSection>
        ))}
      </Shell>
    </Guard>
  );
}

export function MobilePortalProfile() {
  const navigate = useNavigate();
  const session = usePortalSession();
  const { t } = useMobileLocale();
  const query = useQuery({ queryKey: ["m-portal-profile"], queryFn: () => get("/api/storefront/me"), enabled: !!session.token && session.isPortal, staleTime: 15000 });
  const customer = query.data?.data || null;
  const profile = query.data?.profile || null;
  const membership = query.data?.membership || session.portalMembership?.membership || null;
  const customerKind = query.data?.customerKind || session.portalMembership?.customerKind || customer?.customerKind || "";
  const addresses = Array.isArray(profile?.addresses) ? profile.addresses : [];
  const [form, setForm] = useState({ displayName: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!customer && !profile) return;
    setForm({
      displayName: profile?.displayName || customer?.name || "",
      email: profile?.email || customer?.emails?.[0] || "",
      phone: profile?.phone || customer?.phones?.[0] || "",
    });
  }, [customer, profile]);

  const save = async () => {
    try {
      setSaving(true);
      setNotice(null);
      await put("/api/storefront/me", { data: { displayName: form.displayName || null, email: form.email || null, emails: form.email ? [form.email] : [], phone: form.phone || null, phones: form.phone ? [form.phone] : [] } });
      setNotice({ type: "success", text: t("Profile saved.") });
      await query.refetch();
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || t("Unable to save profile.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Guard>
      <Shell title="Profile" subtitle="Contact details.">
        {notice ? (
          <Alert severity={notice.type} sx={{ borderRadius: "14px", mb: 1.15 }}>
            {notice.text}
          </Alert>
        ) : null}
        <AccountSection title="Account">
          <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: -0.35, mb: 0.35 }}>
            {customer?.name || profile?.displayName || t("Account")}
          </Typography>
          <DetailRow label="Role" value={pretty(customerKind || session.role)} first />
          <DetailRow label="Price tier" value={pretty(customer?.priceTier || "retail")} />
          <DetailRow label="Membership" value={pretty(membership?.membershipLevel || membership?.level || "owner")} />
          <DetailRow
            label="Company"
            value={
              session.activeCompany || localStorage.getItem("activeCompany")
                ? String(session.activeCompany || localStorage.getItem("activeCompany")).toUpperCase()
                : t("Not selected")
            }
          />
          <DetailRow
            label="Addresses"
            value={`${addresses.length} ${addresses.length === 1 ? t("address") : t("addresses")}`}
          />
        </AccountSection>
        <AccountSection title="Contact">
          <Stack spacing={1}>
            <TextField
              label={t("Name")}
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              sx={accountFieldSx}
            />
            <TextField
              label={t("Email")}
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              sx={accountFieldSx}
            />
            <TextField
              label={t("Phone")}
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              sx={accountFieldSx}
            />
          </Stack>
        </AccountSection>
        <Box sx={{ mb: 1.15 }}>
          <Tile
            icon="location_on"
            title="Addresses"
            text="Manage saved shipping addresses."
            onClick={() => navigate("/m/account/addresses")}
            tone="red"
          />
        </Box>
        <MDButton
          fullWidth
          variant="contained"
          color="error"
          sx={accountPrimaryBtnSx}
          disabled={saving}
          onClick={save}
        >
          {saving ? t("Saving...") : t("Save profile")}
        </MDButton>
      </Shell>
    </Guard>
  );
}
